import React, { useState } from 'react';
import {
    Button,
    Card,
    Input,
    Media,
    Modal,
    Progress,
    Toggle,
    Typography,
} from '@decub8/ui';

import OnboardingConfirmationSubmit from '@src/components/Modal/OnboardingConfirmationSubmit';
import OnboardingSubmitSuccess from '@src/components/Modal/OnboardingSuccessSubmit';
import { useOnboardingQuestions } from '@src/hooks/pages/useOnboardingQuestions';

export const OnboardingQuestions: React.FC = () => {
    const {
        show,
        handleSubmitClick,
        answers,
        setAnswers,
        compliance_questions,
        submissionSuccess,
        setSubmissionSuccess,
        handleLogOut,
    } = useOnboardingQuestions();

    const [current_index, setCurrentIndex] = useState(0);
    const [show_confirm_modal, setShowConfirmModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const current_question = compliance_questions[current_index];

    const handleOptionChange = (option: string) => {
        setAnswers((prev) => ({
            ...prev,
            [current_index]: { option, other_text: '' },
        }));
    };

    const isActionDisabled = () => {
        const answer = answers[current_index];
        if (!answer?.option) return true;

        const is_other = answer.option.toLowerCase().includes('other');
        if (
            is_other &&
            (!answer.other_text || answer.other_text.trim().length === 0)
        )
            return true;

        return false;
    };

    const goToNext = () => {
        if (current_index < compliance_questions.length - 1) {
            setCurrentIndex((prev) => prev + 1);
        }
    };

    const goToPrevious = () => {
        if (current_index > 0) {
            setCurrentIndex((prev) => prev - 1);
        }
    };

    return (
        <div className="max-w-[580px] mx-auto my-[60px]">
            <div
                className={
                    show_confirm_modal || submitting
                        ? 'pointer-events-none opacity-30 blur-sm'
                        : ''
                }
            >
                <Modal
                    noClose
                    show={show}
                    onClose={() => {}} //doesnt allow to click outside of the modal and close it
                    is_fixed_height={false}
                    className="max-w-[640px] mx-auto overflow-y-auto"
                >
                    <div className="text-center">
                        <Typography
                            variant="primary"
                            size="4xl"
                            allBold
                            className="mb-3"
                        >
                            Additional Questions
                        </Typography>
                        <Typography variant="secondary">
                            To comply with the regulations, we are required to
                            collect these details from you. Please note that
                            these questions are mandatory to proceed with
                            investing on Decubate platform.
                        </Typography>

                        <div className="bg-surface-level-three px-5 h-[1px] my-[32px]" />
                        <div className="flex items-center justify-center">
                            <div className="w-full">
                                <Progress
                                    percent={
                                        ((current_index + 1) /
                                            compliance_questions.length) *
                                        100
                                    }
                                    id="compliance-questions"
                                    height="h-2"
                                    surface="zero"
                                />
                            </div>
                            <div className="ml-2 text-accent text-xs">
                                {`${current_index + 1}/${
                                    compliance_questions.length
                                }`}
                            </div>
                        </div>

                        {current_question && (
                            <div className="px-4">
                                <Typography
                                    variant="primary"
                                    className="my-8 text-left"
                                    allBold
                                >
                                    {current_question.title}
                                </Typography>

                                {current_question.options.map((option) => (
                                    <div className="mb-4" key={option}>
                                        <div className="flex items-center gap-2">
                                            <Toggle
                                                variant="radio"
                                                active={
                                                    answers[current_index]
                                                        ?.option === option
                                                }
                                                onChange={() =>
                                                    handleOptionChange(option)
                                                }
                                            />
                                            <label
                                                onClick={(e) => {
                                                    e.stopPropagation(); // Prevent modal from thinking it's a background click
                                                    handleOptionChange(option);
                                                }}
                                                className="ml-2 cursor-pointer text-primary text-sm"
                                            >
                                                {option}
                                            </label>
                                        </div>
                                        {option
                                            .toLowerCase()
                                            .includes('other') &&
                                            answers[current_index]?.option ===
                                                option && (
                                                <Input
                                                    type="text"
                                                    className="mt-4 rounded w-full bg-surface-level-zero"
                                                    placeholder="Please specify"
                                                    value={
                                                        answers[current_index]
                                                            ?.other_text || ''
                                                    }
                                                    onChange={(e) =>
                                                        setAnswers((prev) => ({
                                                            ...prev,
                                                            [current_index]: {
                                                                ...prev[
                                                                    current_index
                                                                ],
                                                                other_text:
                                                                    e.target
                                                                        .value,
                                                            },
                                                        }))
                                                    }
                                                />
                                            )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Buttons */}
                        <div className="mt-8">
                            {current_index === 0 && (
                                <>
                                    <Button
                                        className="btn-primary w-full mt-[28px]"
                                        onClick={goToNext}
                                        disabled={isActionDisabled()}
                                    >
                                        Next
                                    </Button>
                                    <button
                                        className="mt-4 text-secondary text-sm"
                                        onClick={handleLogOut}
                                    >
                                        Log out
                                    </button>
                                </>
                            )}

                            {current_index > 0 &&
                                current_index <
                                    compliance_questions.length - 1 && (
                                    <>
                                        <div className="flex justify-between gap-4 mt-[32px]">
                                            <Button
                                                variant="custom"
                                                onClick={goToPrevious}
                                                className="bg-surface-level-three text-secondary w-full"
                                            >
                                                <Media
                                                    className="cursor-pointer inline-block mr-2 items-center text-secondary"
                                                    size={4}
                                                    variant="arrow-left"
                                                />
                                                Previous Step
                                            </Button>
                                            <Button
                                                className="btn-primary w-full"
                                                onClick={goToNext}
                                                disabled={isActionDisabled()}
                                            >
                                                Next
                                            </Button>
                                        </div>
                                        <button
                                            className="mt-4 text-secondary text-sm"
                                            onClick={handleLogOut}
                                        >
                                            Log out
                                        </button>
                                    </>
                                )}

                            {current_index ===
                                compliance_questions.length - 1 && (
                                <>
                                    <Card
                                        id="toggle-card"
                                        surface="two"
                                        className="mt-8 mb-5 px-2 py-2"
                                    >
                                        <Toggle
                                            active={
                                                answers.declaration?.option ===
                                                'accept-terms'
                                            }
                                            onChange={() =>
                                                setAnswers((prev) => ({
                                                    ...prev,
                                                    declaration: {
                                                        option: 'accept-terms',
                                                        other_text: '',
                                                    },
                                                }))
                                            }
                                            id="accept-terms"
                                            label="I confirm that the information provided is accurate and true."
                                            variant="checkbox"
                                            className="text-xs"
                                        />
                                    </Card>

                                    <div className="flex justify-between gap-4">
                                        <Button
                                            variant="custom"
                                            onClick={goToPrevious}
                                            className="bg-surface-level-three text-secondary w-full"
                                        >
                                            <Media
                                                className="cursor-pointer inline-block mr-2 items-center text-secondary"
                                                size={4}
                                                variant="arrow-left"
                                            />
                                            Previous Step
                                        </Button>

                                        <Button
                                            className="btn-primary w-full"
                                            onClick={() => {
                                                setShowConfirmModal(true);
                                            }}
                                            disabled={
                                                isActionDisabled() ||
                                                !answers.declaration?.option
                                            }
                                        >
                                            Submit
                                        </Button>
                                    </div>
                                    <button
                                        className="mt-4 text-secondary text-sm"
                                        onClick={handleLogOut}
                                    >
                                        Log out
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </Modal>
            </div>

            <OnboardingSubmitSuccess
                show={submissionSuccess}
                onClose={() => setSubmissionSuccess(false)}
            />

            {show_confirm_modal && (
                <>
                    <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm z-[85]" />
                    <OnboardingConfirmationSubmit
                        show={show_confirm_modal}
                        onClose={() => setShowConfirmModal(false)}
                        onConfirmSubmit={() => {
                            setSubmitting(true);
                            setShowConfirmModal(false);
                            setSubmissionSuccess(true);
                            handleSubmitClick();
                        }}
                    />
                </>
            )}
        </div>
    );
};

export default OnboardingQuestions;
